import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { setBcProtegeConsultingId } from '@portal/actions/onboarding';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import WalkingManAnimation from '@portal/components/WalkingManAnimation';
import { getRouteStackPath } from '@portal/config/routes';
import { ConsultingResponse, ConsultingStatus } from '@portal/enum/consulting';
import { TaxIdentifierType } from '@portal/enum/onboardingType';
import { useNavigation } from '@portal/hooks/useNavigation';
import OnboardingMsApi from '@portal/repositories/onboardingMs';
import { translate } from '@portal/services/i18n';

import './BcProtegeValidationLoading.less';

const PollInterval = 10000;
const PollTimeout = 60000;

const ArcPhaseBouncy = 1800;
const ArcPhaseFinal = 2600;
const ArcReset = 1500;
const ArcCycle = ArcPhaseBouncy * 2 + ArcPhaseFinal + ArcReset;
const TextSwap1 = ArcPhaseBouncy;
const TextSwap2 = ArcPhaseBouncy * 2;
const TextExitDuration = 350;

const LoadingTextKeys = [
  'PAGES.BC_PROTEGE.LOADING.STEP_3',
  'PAGES.BC_PROTEGE.LOADING.STEP_2',
  'PAGES.BC_PROTEGE.LOADING.STEP_1',
];

type LocationState = {
  taxIdentifier?: string;
  accountType?: TaxIdentifierType;
  consultationId?: string;
};

const BcProtegeValidationLoading: React.FC = () => {
  const navigate = useNavigation<LocationState>();
  const dispatch = useDispatch();
  const taxIdentifier = navigate.location.state?.taxIdentifier;
  const accountType = navigate.location.state?.accountType;
  const cancelledRef = useRef(false);
  const [textIndex, setTextIndex] = useState(0);
  const [displayedTextIndex, setDisplayedTextIndex] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    const scheduleCycle = () => {
      if (cancelled) return;
      timers.push(
        setTimeout(() => setTextIndex(1), TextSwap1),
        setTimeout(() => setTextIndex(2), TextSwap2),
        setTimeout(() => {
          if (cancelled) return;
          setTextIndex(0);
          scheduleCycle();
        }, ArcCycle)
      );
    };
    scheduleCycle();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (textIndex === displayedTextIndex) return;
    const swap = setTimeout(
      () => setDisplayedTextIndex(textIndex),
      TextExitDuration
    );
    return () => clearTimeout(swap);
  }, [textIndex, displayedTextIndex]);

  useEffect(() => {
    cancelledRef.current = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let pollTimeout: ReturnType<typeof setTimeout> | null = null;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
      if (pollTimeout) {
        clearTimeout(pollTimeout);
        pollTimeout = null;
      }
    };

    const handleResolution = (
      status: ConsultingStatus,
      response?: ConsultingResponse,
      consultationId?: string
    ) => {
      if (cancelledRef.current) return;

      if (status === ConsultingStatus.ERROR) {
        navigate(getRouteStackPath('AUTH', 'BC_PROTEGE_ERROR'), {
          replace: true,
          state: { taxIdentifier, accountType },
        });
        return;
      }

      if (status !== ConsultingStatus.COMPLETED) return;

      stopPolling();

      if (response === ConsultingResponse.POSITIVE) {
        navigate(getRouteStackPath('AUTH', 'OPEN_ACCOUNT'), {
          replace: true,
          state: { taxIdentifier, accountType },
        });
        return;
      }

      if (response === ConsultingResponse.NEGATIVE) {
        navigate(getRouteStackPath('AUTH', 'BC_PROTEGE_INSTRUCTIONS'), {
          replace: true,
          state: { taxIdentifier, accountType, consultationId },
        });
        return;
      }

      navigate(getRouteStackPath('AUTH', 'BC_PROTEGE_ERROR'), {
        replace: true,
        state: { taxIdentifier, accountType },
      });
    };

    const start = async () => {
      if (!taxIdentifier) {
        navigate(getRouteStackPath('AUTH', 'REGISTER'), { replace: true });
        return;
      }

      try {
        const consultation = await OnboardingMsApi.sendConsulting(
          taxIdentifier
        );
        if (cancelledRef.current) return;

        dispatch(setBcProtegeConsultingId(consultation.id));

        handleResolution(
          consultation.status,
          consultation.response,
          consultation.id
        );

        if (
          consultation.status === ConsultingStatus.COMPLETED ||
          consultation.status === ConsultingStatus.ERROR
        ) {
          return;
        }

        pollInterval = setInterval(async () => {
          try {
            const result = await OnboardingMsApi.getConsulting(consultation.id);
            handleResolution(result.status, result.response, consultation.id);
          } catch {
            if (cancelledRef.current) return;
            stopPolling();
            navigate(getRouteStackPath('AUTH', 'BC_PROTEGE_ERROR'), {
              replace: true,
              state: { taxIdentifier, accountType },
            });
          }
        }, PollInterval);

        pollTimeout = setTimeout(() => {
          if (cancelledRef.current) return;
          stopPolling();
          navigate(getRouteStackPath('AUTH', 'BC_PROTEGE_ERROR'), {
            replace: true,
            state: { taxIdentifier },
          });
        }, PollTimeout);
      } catch {
        if (cancelledRef.current) return;
        navigate(getRouteStackPath('AUTH', 'BC_PROTEGE_ERROR'), {
          replace: true,
          state: { taxIdentifier, accountType },
        });
      }
    };

    start();

    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
  }, [taxIdentifier, accountType, navigate]);

  return (
    <div className="bc-protege-loading">
      <div className="bc-protege-loading__content">
        <div className="bc-protege-loading__animation" aria-hidden="true">
          <WalkingManAnimation className="bc-protege-loading__animation-canvas" />
        </div>
        <div className="bc-protege-loading__copy">
          <ThemedText
            variant="h3"
            fontWeight="bold"
            className="bc-protege-loading__heading"
          >
            {translate('PAGES.BC_PROTEGE.LOADING.HEADING')}
          </ThemedText>
          <p
            className="bc-protege-loading__subline"
            role="status"
            aria-live="polite"
          >
            <span
              className={
                textIndex === displayedTextIndex
                  ? 'bc-protege-loading__subline-text'
                  : 'bc-protege-loading__subline-text bc-protege-loading__subline-text--exiting'
              }
            >
              {translate(LoadingTextKeys[displayedTextIndex])}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BcProtegeValidationLoading;
